
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BusinessTypeSelect } from "./BusinessTypeSelect";
import { CurrencySelect } from "./CurrencySelect";
import { useQuery } from "@tanstack/react-query";

export const CompleteRegistrationForm = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [selectedBusinessType, setSelectedBusinessType] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("FCFA");
  const [isLoading, setIsLoading] = useState(false);

  const { data: businessTypes, isLoading: isLoadingBusinessTypes } = useQuery({
    queryKey: ["businessTypes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_types")
        .select("*");
      
      if (error) {
        toast.error("Erreur lors du chargement des types d'entreprises");
        throw error;
      }
      
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Utilisateur non authentifié");

      const { error: registrationError } = await supabase
        .from("owner_registrations")
        .insert([
          {
            user_id: user.id,
            full_name: fullName,
            company_name: companyName,
            business_type_id: selectedBusinessType,
            currency: selectedCurrency,
          },
        ]);

      if (registrationError) throw registrationError;

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          company_name: companyName,
          business_type_id: selectedBusinessType,
          currency: selectedCurrency,
        },
      });

      if (updateError) throw updateError;

      toast.success("Profil complété avec succès!");
      navigate("/login-owner");
    } catch (error: any) {
      console.error("Erreur lors de la complétion du profil:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="fullName">Nom complet</Label>
        <Input
          id="fullName"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="companyName">Nom de l'entreprise</Label>
        <Input
          id="companyName"
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          required
        />
      </div>

      <BusinessTypeSelect
        businessTypes={businessTypes || []}
        selectedBusinessType={selectedBusinessType}
        onBusinessTypeChange={setSelectedBusinessType}
      />

      <CurrencySelect
        selectedCurrency={selectedCurrency}
        onCurrencyChange={setSelectedCurrency}
      />

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading || !selectedBusinessType}
      >
        {isLoading ? "Enregistrement..." : "Compléter l'inscription"}
      </Button>
    </form>
  );
};
